export const LANGUAGE_STORAGE_KEY = "app_language";

export const THEME_STORAGE_KEY = "app_theme";
// NOTE: NSAI-5822 — which account the cached theme belongs to. The theme key
// survives sign-out on purpose (the sign-in screen keeps the last look), so on
// a shared browser the next account to sign in would otherwise be painted in
// the previous one's theme until its own preferences answer.
export const THEME_OWNER_STORAGE_KEY = "app_theme_owner";

export const ACCESS_TOKEN_STORAGE_KEY = "access_token";
export const REFRESH_TOKEN_STORAGE_KEY = "refresh_token";

// NOTE: NSAI-4838 — the default / summary / image model selections are now
// persisted server-side per (user, project) via GET/PATCH /models/settings, so
// their localStorage keys were removed. Last-used-model is the ONLY model-related
// value that stays device-local (a per-device convenience, not a persisted
// preference).
export const LAST_USED_MODEL_OR_ASSISTANT_UUID_STORAGE_KEY =
  "last_used_model_or_assistant_uuid";

// NOTE: NSAI-3508 — the reasoning mode (Fast/Thinking) the user last picked
// explicitly. New chats start in this mode instead of always falling back to
// Fast; chats with history still derive their mode from their own messages.
export const LAST_USED_REASONING_MODE_STORAGE_KEY = "last_used_reasoning_mode";

export const CURRENT_PROJECT_UUID_STORAGE_KEY = "current_project_uuid";
// NOTE: NSAI-4330 — a deployment base URL the user picked at runtime (mobile
// "Change tenant"). Absent means the build-time default applies. A device
// preference rather than a credential: it survives a sign-out on purpose.
export const API_BASE_URL_OVERRIDE_STORAGE_KEY = "api_base_url_override";

// NOTE: Durable last-used project. Intentionally NOT cleared on logout (unlike
// CURRENT_PROJECT_UUID) so the next login can restore it; validated before use.
export const LAST_USED_PROJECT_UUID_STORAGE_KEY = "last_used_project_uuid";

export const SAML_ORGANIZATION_UUID_STORAGE_KEY = "saml_organization_uuid";

export const ENABLED_INTEGRATIONS_STORAGE_KEY = "enabled_integrations";

export const ENABLED_CALL_ASSISTANT_FEATURES_STORAGE_KEY =
  "enabled_call_assistant_features";

// NOTE: NSAI-5286 — per-browser opt-in for the Anonymization Lab soft launch.
// Only consulted when the server reports isAnonymizationLabEnabledFF: set it to
// "true" by hand (DevTools) and the lab's sidebar item and route appear for that
// browser, while everyone else on the channel still sees nothing. Mirrors the
// call-assistant key above.
export const ENABLED_ANONYMIZATION_LAB_STORAGE_KEY =
  "enabled_anonymization_lab";

// NOTE: NSAI-1249 — per-device opt-in for the mobile chat-search entry point,
// hidden since NSAI-4401. Unlike the two keys above this one gates nothing on
// the server, so a developer can preview the feature before the deployment-wide
// `isMobileChatSearchEnabled` switch reveals it to everyone. Set from the mobile
// dev-settings screen; see @frontend-shared/constants/feature-flags.
export const ENABLED_MOBILE_CHAT_SEARCH_STORAGE_KEY =
  "enabled_mobile_chat_search";

// NOTE: NSAI-6224 — per-device opt-in for the WhatsApp Bot settings section.
// Only consulted while the server reports isWhatsappBotEnabledFF, i.e. the
// channel is live on this deployment but has not been handed to everyone on
// it. Set it to "true" by hand (DevTools on web, the dev-settings screen on
// mobile) and Settings -> Profile -> WhatsApp Bot appears for that device
// alone. Mirrors the two soft-launch keys above.
export const ENABLED_WHATSAPP_BOT_STORAGE_KEY = "enabled_whatsapp_bot";

// NOTE: NSAI-8174 — per-device opt-in for the model subscription connections
// soft launch. Only consulted while the server reports
// isModelSubscriptionConnectionsEnabledFF: set it to "true" by hand (DevTools
// on web, the dev-settings screen on mobile) and the feature's surfaces appear
// for that device alone. Mirrors the soft-launch keys above.
export const ENABLED_MODEL_SUBSCRIPTION_CONNECTIONS_STORAGE_KEY =
  "enabled_model_subscription_connections";

export const AUDIO_TRANSCRIPTION_DISCLAIMER_DECISION_STORAGE_KEY =
  "audio_transcription_disclaimer_decision";

// NOTE: NSAI-4333 — per-device opt-out from the organization's presentation
// brand theme. Absent (or anything but "false") means "apply it", because the
// server applies the org default whenever the flag is omitted from the send
// payload — so only an explicit opt-out has to be remembered.
//
// The KEY is shared, the storage is not: web writes it to `localStorage` and
// mobile to AsyncStorage, so this is a per-device preference on each client
// rather than one that follows the user between them. Naming it here is what
// keeps the two clients from spelling the same preference differently.
export const APPLY_PRESENTATION_THEME_STORAGE_KEY = "apply_presentation_theme";

// NOTE: NSAI-5926 — which collapsible blocks of the chat-history sidebar the
// user folded away: Folders, Archived, Pinned, the "Assistants" section and its
// per-assistant groups, and the date buckets. A per-device UI preference, so it
// lives here rather than on the user record; it holds section keys only, never
// chat content. Shared by web and the mobile drawer through
// @frontend-shared/utils/sidebar-sections.
export const SIDEBAR_COLLAPSED_SECTIONS_STORAGE_KEY =
  "sidebar_collapsed_sections";

// NOTE: NSAI-5926 — an upper bound on the entries kept above. Every entry is
// created by an explicit toggle, so reaching this needs hundreds of distinct
// sections (month buckets accrue one a month, assistant groups one per
// assistant, and a deleted assistant leaves its key behind); the cap keeps a
// long-lived install from growing the value without limit. Oldest entries are
// dropped first — the ones just written are always kept.
export const MAX_PERSISTED_SIDEBAR_SECTIONS = 500 as const;

// NOTE: NSAI-5622 — which of the sidebar's three views is selected (see
// ESidebarView). Its own key rather than an entry in the collapsed-sections
// record above: that record is one shared value, so every writer has to merge
// into it, and a write landing before the store has hydrated would clobber the
// rest. A standalone key has no such hazard.
//
// Per-device, like the keys above, and deliberately not listed as durable on
// mobile — a fresh sign-in starts on Chats.
export const SIDEBAR_VIEW_STORAGE_KEY = "sidebar_view";
